import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
    Box, Typography, Button, Stack, Avatar, IconButton, Tooltip, InputBase, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, Snackbar, Alert,
} from '@mui/material';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { PRIMARY, PRIMARY_LIGHT, PRIMARY_DARK } from '../../theme';
import { fmtDate, initialsFromName as initials, avatarTone } from '../../utils/format';
import { fmtSize } from '../../utils/fileStore';
import { ghostBtn, successBtn, field, Panel, EmptyState, FileBadge, SkeletonRows, LoadAlert } from '../uiKit';
import http, { apiErrorMessage } from '../../Api/http';
import { GetPendingApprovals, UpdateApprovalAction, PostApproveAll, DownloadSubmission } from '../../Api/Api';
import { fetchFileBlob, saveBlob, blobErrorMessage } from '../../utils/download';

// A fixed track per column so rows line up down the page; a wrapping flex row
// let every submission pick its own gaps and the list read as ragged.
const COLS = 'minmax(190px, 1.2fr) minmax(180px, 1.1fr) minmax(210px, 1.3fr) 300px';
const MIN_TABLE_WIDTH = 940;
const PAD_X = 2.2;

function ColLabel({ children, align }) {
    return (
        <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#5B7A94', textTransform: 'uppercase', letterSpacing: 0.6, textAlign: align || 'left' }}>
            {children}
        </Typography>
    );
}

// Same soft approve/reject pair the punch-request table uses, so a decision
// looks the same wherever it is made.
const approveBtn = {
    bgcolor: '#F0FDF4', color: '#15803D', border: '1px solid #C4EBD1',
    fontWeight: 700, textTransform: 'none', borderRadius: '7px', boxShadow: 'none',
    '&:hover': { bgcolor: '#DCFCE7', borderColor: '#9FDDB4' },
    '&.Mui-disabled': { bgcolor: '#F8FAFC', color: '#B6BDC9', borderColor: '#EEF0F6' },
};
const rejectBtn = {
    bgcolor: '#fff', color: '#BE123C', border: '1px solid #F3D3DA',
    fontWeight: 700, textTransform: 'none', borderRadius: '7px', boxShadow: 'none',
    '&:hover': { bgcolor: '#FEF3F5', borderColor: '#EEB9C4' },
    '&.Mui-disabled': { bgcolor: '#F8FAFC', color: '#B6BDC9', borderColor: '#EEF0F6' },
};

const isoFromDMY = (s) => {
    const m = String(s || '').match(/^(\d{2})-(\d{2})-(\d{4})$/);
    return m ? `${m[3]}-${m[2]}-${m[1]}` : (s || '');
};

const normalizeApproval = (it) => ({
    recipientId: it.recipientId,
    requestId: it.requestId,
    employeeId: it.employeeCode ?? '',
    employeeName: it.employeeName ?? '',
    department: it.department ?? '',
    requestTitle: it.documentName ?? '',
    category: it.category ?? '',
    requestedBy: it.requestedByName ?? '',
    requestedOn: isoFromDMY(it.requestedOn),
    dueDate: isoFromDMY(it.dueDate),
    fileName: it.fileName ?? '',
    fileSize: Number(it.sizeBytes) || 0,
    submittedOn: isoFromDMY(it.submittedOn),
    remark: it.employeeNote ?? '',
});

export default function DocumentApprovalsTab() {
    const [pending, setPending] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [acting, setActing] = useState(null);

    const [q, setQ] = useState('');
    const [reject, setReject] = useState(null);
    const [reason, setReason] = useState('');
    const [downloading, setDownloading] = useState(null);
    const [snack, setSnack] = useState(null);

    const notify = (msg, sev = 'success') => setSnack({ msg, sev });

    const loadApprovals = useCallback(async () => {
        setLoading(true);
        try {
            const { data: body } = await http.get(GetPendingApprovals);
            if (body?.error) throw new Error(body.message || 'Could not load pending approvals.');
            const items = Array.isArray(body?.data?.items) ? body.data.items : [];
            setPending(items.map(normalizeApproval));
            setLoadError('');
        } catch (err) {
            setLoadError(apiErrorMessage(err, 'Could not load pending approvals.'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadApprovals(); }, [loadApprovals]);

    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        if (!s) return pending;
        return pending.filter((p) => [p.employeeName, p.employeeId, p.requestTitle, p.department].some((v) => String(v || '').toLowerCase().includes(s)));
    }, [pending, q]);

    const approve = async (p) => {
        if (acting) return;
        setActing(p.recipientId);
        try {
            const { data: body } = await http.put(UpdateApprovalAction, { recipientId: p.recipientId, action: 'approve' });
            if (body?.error) throw new Error(body.message || 'Could not approve this submission.');
            notify(body?.message || `${p.requestTitle} approved — filed under ${p.employeeName}.`);
            await loadApprovals();
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not approve this submission.'), 'error');
        } finally {
            setActing(null);
        }
    };

    const approveAll = async () => {
        if (acting || filtered.length === 0) return;
        setActing('all');
        try {
            const { data: body } = await http.post(PostApproveAll, { recipientIds: filtered.map((p) => p.recipientId) });
            if (body?.error) throw new Error(body.message || 'Could not approve all submissions.');
            notify(body?.message || `${filtered.length} submission${filtered.length === 1 ? '' : 's'} approved and filed.`);
            await loadApprovals();
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not approve all submissions.'), 'error');
        } finally {
            setActing(null);
        }
    };

    const doReject = async () => {
        if (!reject || acting) return;
        setActing(reject.recipientId);
        try {
            const { data: body } = await http.put(UpdateApprovalAction, {
                recipientId: reject.recipientId,
                action: 'reject',
                reason: reason.trim(),
            });
            if (body?.error) throw new Error(body.message || 'Could not reject this submission.');
            notify(body?.message || `${reject.requestTitle} rejected — ${reject.employeeName} can re-upload.`);
            setReject(null);
            setReason('');
            await loadApprovals();
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not reject this submission.'), 'error');
        } finally {
            setActing(null);
        }
    };

    const download = async (p) => {
        if (downloading) return;
        setDownloading(p.recipientId);
        try {
            const { blob, name } = await fetchFileBlob(DownloadSubmission, { recipientId: p.recipientId });
            saveBlob(blob, name || p.fileName);
        } catch (err) {
            notify(await blobErrorMessage(err, 'Could not download the file.'), 'error');
        } finally {
            setDownloading(null);
        }
    };

    const [preview, setPreview] = useState(null);
    const [viewing, setViewing] = useState(null);

    const closePreview = () => {
        setPreview((pv) => { if (pv?.url) URL.revokeObjectURL(pv.url); return null; });
    };

    const view = async (p) => {
        if (viewing) return;
        setViewing(p.recipientId);
        try {
            const { blob, name } = await fetchFileBlob(DownloadSubmission, { recipientId: p.recipientId });
            const fname = name || p.fileName || '';
            const ext = fname.split('.').pop().toLowerCase();
            const EXT_MIME = {
                pdf: 'application/pdf',
                png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp', bmp: 'image/bmp',
            };
            const generic = !blob.type || blob.type === 'application/octet-stream';
            const mime = (generic && EXT_MIME[ext]) ? EXT_MIME[ext] : (blob.type || EXT_MIME[ext] || '');
            const typed = mime && mime !== blob.type ? new Blob([blob], { type: mime }) : blob;
            setPreview({ p, url: URL.createObjectURL(typed), mime, name: fname });
        } catch (err) {
            notify(await blobErrorMessage(err, 'Could not open the file.'), 'error');
        } finally {
            setViewing(null);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Panel
                title="Pending Approvals"
                icon={FactCheckRoundedIcon}
                chip={`${pending.length} awaiting review`}
                chipColor="#0369A1"
                chipBg="#E0F2FE"
                action={(
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', bgcolor: '#fff', border: '1px solid #E6EAF1', borderRadius: '7px', px: 1.4, height: 38, width: { xs: 150, sm: 220 } }}>
                            <SearchRoundedIcon sx={{ fontSize: 18, color: '#98A0AE' }} />
                            <InputBase placeholder="Search submissions…" value={q} onChange={(e) => setQ(e.target.value)} sx={{ fontSize: 13, flex: 1 }} />
                        </Stack>
                        <Tooltip arrow title="Reload">
                            <IconButton onClick={loadApprovals} disabled={loading} sx={{ border: '1px solid #E6EAF1', borderRadius: '7px', color: '#64748B', height: 38, width: 38, '&:hover': { bgcolor: PRIMARY_LIGHT, color: PRIMARY } }}>
                                <RefreshRoundedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                        {filtered.length > 1 && (
                            <Button onClick={approveAll} disabled={Boolean(acting)}
                                startIcon={acting === 'all' ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : <DoneAllRoundedIcon sx={{ fontSize: 17 }} />}
                                sx={{ ...successBtn, height: 38, px: 1.6, fontSize: 12.5 }}>
                                {acting === 'all' ? 'Approving…' : `Approve all (${filtered.length})`}
                            </Button>
                        )}
                    </Stack>
                )}
            >
                {loading ? (
                    <SkeletonRows count={5} />
                ) : loadError ? (
                    <Box sx={{ p: 3 }}>
                        <LoadAlert sx={{ borderRadius: '9px' }}
                            action={<Button size="small" onClick={loadApprovals} sx={{ textTransform: 'none', fontWeight: 700 }}>Retry</Button>}>
                            {loadError}
                        </LoadAlert>
                    </Box>
                ) : filtered.length === 0 ? (
                    <EmptyState
                        icon={CheckRoundedIcon}
                        title={q ? 'Nothing matches that search' : 'Nothing waiting on you'}
                        hint={q ? 'Try another employee or document name.' : 'Every submitted document has been reviewed. New uploads land here automatically.'}
                    />
                ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                        <Box sx={{ minWidth: MIN_TABLE_WIDTH }}>
                            <Box sx={{
                                display: 'grid', gridTemplateColumns: COLS, alignItems: 'center', gap: 2,
                                px: PAD_X, py: 1.3, bgcolor: '#FAFAFD', borderBottom: '1px solid #E6EAF1',
                            }}>
                                <ColLabel>Employee</ColLabel>
                                <ColLabel>Document</ColLabel>
                                <ColLabel>Submission</ColLabel>
                                <ColLabel align="right">Decision</ColLabel>
                            </Box>

                            {filtered.map((p) => {
                                const busy = acting === p.recipientId;
                                return (
                                    <Box
                                        key={p.recipientId}
                                        sx={{
                                            display: 'grid', gridTemplateColumns: COLS, alignItems: 'center', gap: 2,
                                            px: PAD_X, py: 1.7, borderBottom: '1px solid #EEF0F6',
                                            opacity: busy ? 0.55 : 1, transition: 'background-color .15s, opacity .15s',
                                            '&:hover': { bgcolor: '#F3F8FC' },
                                            '&:last-of-type': { borderBottom: 'none' },
                                        }}
                                    >
                                        <Stack direction="row" spacing={1.3} sx={{ alignItems: 'center', minWidth: 0 }}>
                                            <Avatar sx={{ width: 38, height: 38, ...avatarTone(p.employeeName), fontSize: 13, fontWeight: 700, flexShrink: 0 }}>{initials(p.employeeName)}</Avatar>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#0F172A' }} noWrap>{p.employeeName}</Typography>
                                                <Typography sx={{ fontSize: 11.5, color: '#98A0AE' }} noWrap>{p.employeeId}{p.department ? ` · ${p.department}` : ''}</Typography>
                                            </Box>
                                        </Stack>

                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: PRIMARY_DARK }} noWrap>{p.requestTitle}</Typography>
                                            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', mt: 0.2 }}>
                                                <PersonRoundedIcon sx={{ fontSize: 13, color: '#CBD2DD', flexShrink: 0 }} />
                                                <Typography sx={{ fontSize: 11, color: '#98A0AE' }} noWrap>{p.requestedBy} · {fmtDate(p.requestedOn)}</Typography>
                                            </Stack>
                                            {p.dueDate && (
                                                <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', mt: 0.2 }}>
                                                    <ScheduleRoundedIcon sx={{ fontSize: 13, color: '#CBD2DD', flexShrink: 0 }} />
                                                    <Typography sx={{ fontSize: 11, color: '#98A0AE' }} noWrap>Due {fmtDate(p.dueDate)}</Typography>
                                                </Stack>
                                            )}
                                        </Box>

                                        <Box sx={{ minWidth: 0 }}>
                                            <FileBadge fileName={p.fileName} fileSize={p.fileSize} sub={`${fmtSize(p.fileSize)} · ${fmtDate(p.submittedOn)}`} />
                                            {p.remark && (
                                                <Typography sx={{ fontSize: 11.5, color: '#64748B', mt: 0.5, fontStyle: 'italic' }} noWrap>“{p.remark}”</Typography>
                                            )}
                                        </Box>

                                        <Stack direction="row" spacing={0.8} sx={{ justifyContent: 'flex-end', alignItems: 'center' }}>
                                            <Tooltip arrow title="Open the file">
                                                <span>
                                                    <IconButton onClick={() => view(p)} disabled={viewing === p.recipientId}
                                                        sx={{ width: 32, height: 32, borderRadius: '7px', color: PRIMARY, bgcolor: PRIMARY_LIGHT, border: '1px solid #C2DCEE', '&:hover': { bgcolor: '#D0E5F3' } }}>
                                                        {viewing === p.recipientId ? <CircularProgress size={14} /> : <VisibilityRoundedIcon sx={{ fontSize: 17 }} />}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip arrow title="Download">
                                                <span>
                                                    <IconButton onClick={() => download(p)} disabled={downloading === p.recipientId}
                                                        sx={{ width: 32, height: 32, borderRadius: '7px', color: '#64748B', border: '1px solid #E6EAF1', '&:hover': { color: PRIMARY, bgcolor: PRIMARY_LIGHT } }}>
                                                        {downloading === p.recipientId ? <CircularProgress size={14} /> : <DownloadRoundedIcon sx={{ fontSize: 17 }} />}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Button onClick={() => approve(p)} disabled={Boolean(acting)}
                                                startIcon={busy ? <CircularProgress size={14} sx={{ color: '#15803D' }} /> : <CheckRoundedIcon sx={{ fontSize: 16 }} />}
                                                sx={{ ...approveBtn, height: 32, px: 1.4, fontSize: 12.5 }}>Approve</Button>
                                            <Button onClick={() => { setReject(p); setReason(''); }} disabled={Boolean(acting)}
                                                startIcon={<CloseRoundedIcon sx={{ fontSize: 16 }} />}
                                                sx={{ ...rejectBtn, height: 32, px: 1.4, fontSize: 12.5 }}>Reject</Button>
                                        </Stack>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                )}
            </Panel>

            <Dialog open={Boolean(preview)} onClose={closePreview} maxWidth="md" fullWidth slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
                <DialogTitle sx={{ pb: 0.5 }}>
                    <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 17, fontWeight: 800, color: '#0F172A' }} noWrap>{preview?.p?.requestTitle}</Typography>
                            <Typography sx={{ fontSize: 12.5, color: '#6B7280', mt: 0.2 }} noWrap>{preview?.p?.employeeName} · {preview?.name}</Typography>
                        </Box>
                        <IconButton onClick={closePreview} size="small"><CloseRoundedIcon /></IconButton>
                    </Stack>
                </DialogTitle>
                <DialogContent sx={{ p: 0, bgcolor: '#0F172A08' }}>
                    <Box sx={{ minHeight: 300, maxHeight: '68vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1.5, overflow: 'auto' }}>
                        {preview && (preview.mime.startsWith('image/') ? (
                            <Box component="img" src={preview.url} alt={preview.name} sx={{ maxWidth: '100%', maxHeight: '64vh', borderRadius: '8px', boxShadow: '0 8px 24px rgba(16,24,40,0.18)' }} />
                        ) : preview.mime === 'application/pdf' ? (
                            <Box component="iframe" title={preview.name} src={preview.url} sx={{ width: '100%', height: '64vh', border: 'none', borderRadius: '8px' }} />
                        ) : (
                            <Box sx={{ textAlign: 'center', py: 5 }}>
                                <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#334155' }}>Preview not available for this file type</Typography>
                                <Typography sx={{ fontSize: 12.5, color: '#94A3B8', mt: 0.5 }}>Download it to review, then approve or reject.</Typography>
                            </Box>
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #F1F3F7', flexWrap: 'wrap', gap: 1 }}>
                    <Button onClick={() => preview && download(preview.p)} startIcon={<DownloadRoundedIcon sx={{ fontSize: 17 }} />} sx={{ ...ghostBtn, height: 40, px: 2 }}>Download</Button>
                    <Box sx={{ flex: 1 }} />
                    <Button onClick={() => { const p = preview?.p; closePreview(); if (p) { setReject(p); setReason(''); } }} disabled={Boolean(acting)} startIcon={<CloseRoundedIcon sx={{ fontSize: 17 }} />} sx={{ ...rejectBtn, height: 40, px: 2 }}>Reject</Button>
                    <Button onClick={() => { const p = preview?.p; closePreview(); if (p) approve(p); }} disabled={Boolean(acting)} startIcon={<CheckRoundedIcon sx={{ fontSize: 17 }} />} sx={{ ...approveBtn, height: 40, px: 2.4 }}>Approve</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(reject)} onClose={() => { if (!acting) setReject(null); }} maxWidth="xs" fullWidth slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
                <DialogTitle sx={{ pb: 0.5 }}>
                    <Typography sx={{ fontSize: 17, fontWeight: 800, color: '#0F172A' }}>Reject submission</Typography>
                    <Typography sx={{ fontSize: 12.5, color: '#6B7280', mt: 0.2 }}>{reject?.employeeName} · {reject?.requestTitle}</Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: '8px !important' }}>
                    <TextField label="Reason" size="small" fullWidth multiline minRows={3} autoFocus
                        placeholder="e.g. The scan is cut off — please re-upload the full page."
                        value={reason} onChange={(e) => setReason(e.target.value)} sx={field} />
                    <Typography sx={{ fontSize: 11.5, color: '#98A0AE', mt: 1 }}>They will be asked to upload it again with this reason attached.</Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setReject(null)} disabled={Boolean(acting)} sx={{ ...ghostBtn, height: 40, px: 2 }}>Cancel</Button>
                    <Button onClick={doReject} disabled={Boolean(acting) || !reason.trim()}
                        startIcon={acting && reject ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : null}
                        sx={{ height: 40, px: 2.4, fontWeight: 700, textTransform: 'none', borderRadius: '7px', bgcolor: '#DC2626', color: '#fff', '&:hover': { bgcolor: '#B91C1C' }, '&.Mui-disabled': { bgcolor: '#FCA5A5', color: '#fff' } }}>
                        {acting && reject ? 'Rejecting…' : 'Reject'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={Boolean(snack)} autoHideDuration={3600} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert onClose={() => setSnack(null)} severity={snack?.sev || 'success'} variant="filled" sx={{ borderRadius: '7px' }}>{snack?.msg}</Alert>
            </Snackbar>
        </Box>
    );
}
